import { useEffect, useMemo } from "react";
import {
  DocumentViewer,
  MiDragAndDropFileUpload,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  FILE_MIME_TYPE_EXTENSIONS,
  NOT_APPLICABLE,
} from "@miplatform/constants";
import { IAttempt, IStep, StepStatus } from "@miplatform/types";
import { mdiTrashCanOutline } from "@mdi/js";
import { DocumentCard } from "@mibuildweb/components";
import { useTheme } from "@mui/material";
import { useWorkOrderDocumentStepViewModel } from "./useWorkOrderDocumentStepViewModel";

export const WorkOrderDocumentStep = ({
  step,
  isDisabled,
  isEditMode,
  onRefresh = () => {},
  attempt = null,
}: WorkOrderDocumentStepProps) => {
  const theme = useTheme();
  const isCompleted =
    step.status === StepStatus.Completed ||
    step.stepResult?.status === StepStatus.Completed;
  const documents = useMemo(
    () =>
      isEditMode
        ? step?.stepResult?.documents || []
        : [...step.documents, ...(step?.stepResult?.documents || [])],
    [isEditMode, step?.stepResult?.documents, step.documents]
  );

  const {
    isDeleteModalOpen,
    selectedDocument,
    deleteFile,
    loading,
    documentCategories,
    uploadFiles,
    setDeleteDocument,
    printFile,
    getDocumentCategory,
    deleteDocument,
    downloadFile,
    handleDocumentSelect,
    handleDeleteModalClose,
  } = useWorkOrderDocumentStepViewModel({
    attempt,
    step,
    onRefresh,
  });
  const allowedFileFormats = useMemo(() => {
    const documentCategory =
      documentCategories.find(
        (category) => category.id === step.documentCategoryId
      ) || documentCategories[0];

    return documentCategory?.allowedFileFormats || [];
  }, [documentCategories, step.documentCategoryId]);

  useEffect(() => {
    getDocumentCategory();
  }, []);

  return (
    <>
      {!isDisabled && (!isCompleted || isEditMode) && (
        <MiDragAndDropFileUpload
          showUploadButton
          onChange={uploadFiles}
          disabled={loading || documentCategories.length === 0}
          validFileTypes={allowedFileFormats}
          acceptableFormatText={`Acceptable formats: ${
            allowedFileFormats
              ?.map((mimeType: string) => FILE_MIME_TYPE_EXTENSIONS[mimeType])
              .join(", ") || NOT_APPLICABLE
          }`}
        />
      )}
      {(step?.stepResult?.documents?.length || 0) > 0 && (
        <MiList
          hideListPagination
          viewType="card"
          data={documents}
          CardComponent={DocumentCard}
          cardListContainerProps={{
            style: { padding: 0, marginTop: theme.spacing(2) },
          }}
          cardProps={{
            hideUploadedBy: true,
            onPrint: printFile,
            onDelete: setDeleteDocument,
            onDownload: downloadFile,
            onSelect: handleDocumentSelect,
            selectedDoc: selectedDocument,
            disableDelete: isDisabled,
          }}
          loading={loading}
        />
      )}
      <DocumentViewer
        fullScreenStatus={!!selectedDocument}
        documents={[
          selectedDocument,
          ...documents.filter((d) => d.id !== selectedDocument?.id),
        ]}
        onUnfullscreen={() => handleDocumentSelect(null)}
      />
      <MiModalConfirm
        title="Delete Document"
        description={`Are you sure you want to delete document ${
          deleteFile?.metadata?.title || deleteFile?.filename
        }?`}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.error.main,
        }}
        open={isDeleteModalOpen}
        disableClick={loading}
        handlePositive={deleteDocument}
        handleNegative={handleDeleteModalClose}
      />
    </>
  );
};

interface WorkOrderDocumentStepProps {
  step: IStep;
  isDisabled?: boolean;
  isEditMode?: boolean;
  onRefresh: () => void;
  attempt: IAttempt | null;
}
